import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Box, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, ContentCopy as CopyIcon, Delete as DeleteIcon } from '@mui/icons-material';
import FormDialog from '../../common/FormDialog';
import { gradingAPI } from '../../../services/api';

const EMPTY_ROW = () => ({ grade: '', min_percentage: '', max_percentage: '', grade_point: '', aggregate_value: '', description: '' });

const BulkGradeBoundaryDialog = ({ open, onClose, onSubmit, saving, scales = [], selectedScale = '', isMobile = false }) => {
  const [scaleId, setScaleId] = useState('');
  const [sourceScaleId, setSourceScaleId] = useState('');
  const [rows, setRows] = useState([EMPTY_ROW()]);

  useEffect(() => {
    if (open) { setScaleId(selectedScale || ''); setSourceScaleId(''); setRows([EMPTY_ROW()]); }
  }, [open, selectedScale]);

  const loadFromScale = async (srcId) => {
    setSourceScaleId(srcId);
    if (!srcId) return;
    try {
      const res = await gradingAPI.getBoundariesForScale(srcId);
      const data = Array.isArray(res.data) ? res.data : res.data.data || [];
      if (data.length) {
        setRows(data.map((b) => ({
          grade: b.grade,
          min_percentage: b.min_percentage,
          max_percentage: b.max_percentage,
          grade_point: b.grade_point ?? '',
          aggregate_value: b.aggregate_value ?? '',
          description: b.description ?? '',
        })));
      }
    } catch { /* silent */ }
  };

  const update = (i, field, value) =>
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const handleSubmit = (e) => {
    e.preventDefault();
    const valid = rows.filter((r) => r.grade && r.min_percentage !== '' && r.max_percentage !== '');
    if (!valid.length || !scaleId) return;
    onSubmit(valid.map((r) => ({ ...r, grading_scale_id: Number(scaleId) })), () => setRows([EMPTY_ROW()]));
  };

  const canSave = scaleId && rows.some((r) => r.grade && r.min_percentage !== '' && r.max_percentage !== '');

  const overlapSet = new Set();
  const sorted = [...rows]
    .map((r, i) => ({ ...r, i }))
    .filter((r) => r.min_percentage !== '' && r.max_percentage !== '')
    .sort((a, b) => Number(a.min_percentage) - Number(b.min_percentage));
  for (let x = 0; x < sorted.length - 1; x++) {
    if (Number(sorted[x].max_percentage) >= Number(sorted[x + 1].min_percentage)) {
      overlapSet.add(sorted[x].i);
      overlapSet.add(sorted[x + 1].i);
    }
  }

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Bulk Add Grade Boundaries"
      saving={saving}
      disabled={!canSave}
      isMobile={isMobile}
      maxWidth="lg"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Autocomplete
            fullWidth
            options={scales}
            value={scales.find((s) => Number(s.id) === Number(scaleId)) || null}
            onChange={(_, v) => setScaleId(v?.id || '')}
            getOptionLabel={(o) => `${o.scale_name} (${o.scale_code})`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} label="Target Scale (save to)" required />}
          />
          <Autocomplete
            fullWidth
            options={scales.filter((s) => Number(s.id) !== Number(scaleId))}
            value={scales.find((s) => Number(s.id) === Number(sourceScaleId)) || null}
            onChange={(_, v) => loadFromScale(v?.id || '')}
            getOptionLabel={(o) => `${o.scale_name} (${o.scale_code})`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Copy grades from scale (optional)"
                InputProps={{ ...params.InputProps, startAdornment: <Tooltip title="Pre-fills rows from an existing scale — adjust min/max as needed"><CopyIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /></Tooltip> }}
              />
            )}
          />
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Grade *</TableCell>
                <TableCell>Min % *</TableCell>
                <TableCell>Max % *</TableCell>
                <TableCell>Grade Point</TableCell>
                <TableCell>Aggregate</TableCell>
                <TableCell>Description</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} sx={overlapSet.has(i) ? { bgcolor: 'error.lighter' } : {}}>
                  <TableCell><TextField size="small" value={row.grade} onChange={(e) => update(i, 'grade', e.target.value)} inputProps={{ maxLength: 10 }} placeholder="e.g. D1" sx={{ width: 70 }} /></TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={row.min_percentage}
                      onChange={(e) => update(i, 'min_percentage', e.target.value)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      error={row.max_percentage !== '' && row.min_percentage !== '' && Number(row.max_percentage) < Number(row.min_percentage)}
                      sx={{ width: 80 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={row.max_percentage}
                      onChange={(e) => update(i, 'max_percentage', e.target.value)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      error={row.max_percentage !== '' && row.min_percentage !== '' && Number(row.max_percentage) < Number(row.min_percentage)}
                      helperText={row.max_percentage !== '' && row.min_percentage !== '' && Number(row.max_percentage) < Number(row.min_percentage) ? `Must be ≥ ${row.min_percentage}` : ''}
                      sx={{ width: 80 }} />
                  </TableCell>
                  <TableCell><TextField size="small" type="number" value={row.grade_point} onChange={(e) => update(i, 'grade_point', e.target.value)} inputProps={{ min: 0, max: 9.99, step: 0.01 }} sx={{ width: 80 }} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={row.aggregate_value} onChange={(e) => update(i, 'aggregate_value', e.target.value)} inputProps={{ min: 0, step: 1 }} sx={{ width: 80 }} /></TableCell>
                  <TableCell><TextField size="small" value={row.description} onChange={(e) => update(i, 'description', e.target.value)} sx={{ width: 140 }} /></TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} disabled={rows.length === 1}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box>
          <IconButton size="small" color="primary" onClick={() => setRows((p) => [...p, EMPTY_ROW()])}><AddIcon /></IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>Add row</Typography>
        </Box>
      </Stack>
    </FormDialog>
  );
};

export default BulkGradeBoundaryDialog;
